import React from "react";
import Seo from "../components/Seo";
import { Hero, Section, Band } from "../components/Section";
import { CompareTable, Numbered } from "../components/Table";
import { ShieldCheck, Landmark, HeartHandshake, Scale, UserRound, Database, Workflow, Cpu, TrendingUp, TrendingDown } from "lucide-react";
import { EditorialIcon } from "../components/EditorialVisuals";
import { PathDiagram } from "../components/EditorialDiagrams";

const LEAD =
  "Syni.ai is a pan-African applied AI research and product studio, headquartered in Nairobi. Our mission is to help enterprises adopt and scale the use of AI safely.";

const PATHS = [
  {
    title: "Technology.",
    body: "We build governance instrumentation: the gates, engines and insurance that confine AI to what it has been permitted to do, touch and see. Our first product is Aky, AI in a Box.",
  },
  {
    title: "Capacity.",
    body: "We raise AI awareness and the executive capacity to govern it, through Lead.ai and the Aspen + Syni.ai Executive Fellowship on AI Adoption and Governance.",
  },
  {
    title: "Research.",
    body: "We publish what we learn as we build and as enterprises adopt: benchmarks, training material and content that contextualise AI for local use.",
  },
];

const PROTECTS = [
  { title: "Safety and security.", body: "People, systems and information kept from harm." },
  { title: "Preservation of wealth.", body: "Assets, data and intellectual property stay with their owners." },
  { title: "Continuance of well-being.", body: "Work, culture and daily life keep functioning." },
  { title: "Balanced beneficiation.", body: "The gains are shared by those who bear the risk." },
];

const CREATING = [
  "work that took teams now takes hours",
  "discovery moves faster",
  "expertise reaches people and places that never had it",
  "new markets and new work appear",
  "decisions happen in a fraction of the time",
];

const ERODING = [
  "data leaves and trains what you do not own",
  "confident errors and models that drift",
  "systems acting beyond their mandate",
  "power and gains pooling with a few makers",
  "output generated faster than people can absorb it",
];

const GOVERNING = [
  { title: "Human intent.", body: "The intentions of the makers and users of AI. Human activity with AI carries the greatest risk and the greatest reward." },
  { title: "Access to data.", body: "What AI can see, where it can take it, and what it is allowed to learn from." },
  { title: "Disruption of culture and ways of work.", body: "How roles, skills and norms change, and who decides the pace." },
  { title: "AI capability.", body: "What the system is able to do, and the limits placed on it." },
];

const AFRICA = [
  { title: "Protect Africa's data.", body: "What is created here stays owned here." },
  { title: "Contextualise AI for local use,", body: "without the baggage of the AI race." },
  { title: "Minimise the risks of adoption at scale.", body: "Controls in place before rollout, not after." },
  { title: "Realise the gains faster.", body: "Confidence to deploy, because the box is yours." },
  { title: "Own the redefinition of work.", body: "Decide locally how roles and skills change." },
];

export default function About() {
  return (
    <>
      <Seo title="About Syni.ai" description={LEAD} path="/about" />
      <Hero title="About Syni.ai" lead={LEAD} testId="about-hero" />

      <Section n="01" label="Who we are" split testId="about-who" visual={<PathDiagram testId="about-path-diagram" />}>
        <div className="prose body-copy measure" style={{ marginBottom: 40 }}>
          <p>
            Founded by senior executives and technology operators, we build the technology, the executive capacity and the evidence that let
            organisations validate and scale AI on their own data without giving up governance or data sovereignty. We work in three paths,
            and every path serves the same end: AI working inside a box the organisation controls.
          </p>
        </div>
        <Numbered items={PATHS} testId="about-paths" />
      </Section>

      <Section n="02" label="What governance means to us" split pale testId="about-governance">
        <p className="definition" data-testid="governance-definition">
          Governance is a system of rules, practices, processes, controls and structures put in place by a country, community or organisation
          to ensure safety and security, preservation of wealth, continuance of well-being and balanced beneficiation. Anything that threatens
          to change the order of things, putting that balance at risk, ought to be governed.
        </p>
        <p className="body-copy" style={{ marginTop: 40, marginBottom: 20 }}>
          Governance protects four things:
        </p>
        <Numbered items={PROTECTS} icons={[ShieldCheck, Landmark, HeartHandshake, Scale]} testId="about-protects" />
      </Section>

      <Section n="03" label="Why governance is needed now" testId="about-why">
        <div className="contrast" data-testid="about-contrast">
          <div>
            <h3 className="contrast-head icon-heading" data-testid="about-value-created-title"><EditorialIcon icon={TrendingUp} testId="about-value-created-icon" />AI is creating value:</h3>
            <ul className="contrast-list">
              {CREATING.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="contrast-head icon-heading" data-testid="about-value-eroded-title"><EditorialIcon icon={TrendingDown} testId="about-value-eroded-icon" />It is eroding value at the same time:</h3>
            <ul className="contrast-list">
              {ERODING.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
        </div>
        <p className="body-copy contrast-close measure">If the second list is not managed, the first will not be appreciated or sustained.</p>
      </Section>

      <Section n="04" label="Regulation versus insulation" split pale testId="about-compare" below={<CompareTable testId="about-compare-table" />}>
        <p className="body-copy measure">
          Because of the vested interests in the race to dominate AI, governance needs to be more than regulation. It needs insulation.
        </p>
      </Section>

      <Section n="05" label="What needs governing" title="Four things, in order of how much risk and reward they carry." split testId="about-governing">
        <Numbered items={GOVERNING} icons={[UserRound, Database, Workflow, Cpu]} testId="about-governing-list" />
      </Section>

      <Section
        n="06"
        label="For Africa and other emerging markets"
        title="Regulation is written by those who can afford to shape AI. Insulation is built by those who have to live with it."
        split
        pale
        testId="about-africa"
      >
        <p className="body-copy measure" style={{ marginBottom: 32 }}>
          For Africa, that is the lever within reach. It means five things:
        </p>
        <Numbered items={AFRICA} testId="about-africa-list" />
      </Section>

      <Band label="Talk to us" links={[{ to: "/contact", label: "Contact", testId: "about-close-contact" }]} testId="about-close" />
    </>
  );
}
